/**
 * The intake's IndexedDB — hand-rolled like the T1 spike's `handleStore.ts`
 * (no `idb` / `idb-keyval`; DI-2-1 adds exactly one dependency, spark-md5).
 *
 * Object stores in one database:
 *   handles   key = handle key → FileSystemDirectoryHandle (structured-cloneable)
 *   sessions  key = sessionId  → SessionRecord
 *   meta      key = a name     → a small value (v2, DI-3-C). DI-3-D keeps the
 *             browser's active session here (`activeSession`, N8: resume from
 *             any tab — `sessionStorage` is no longer read) and the runner's
 *             one-tab claim (`runnerClaim`, `runner/claim.ts`)
 *
 * v1 → v2 only ADDS `meta`: an upgrade never touches `handles` or `sessions`.
 *
 * v2 → v3 (M3C-04) MIGRATES the sessions: every cluster gains
 * `targetTenantSlug` (the tenant the set lives in) — `null` = the caller's
 * own, which is what every set created before M3C-04 is (a shared drop did
 * not exist). The rows are rewritten in the upgrade transaction, so a tab
 * never reads a v3 database holding an unmigrated record.
 */

export const DB_NAME = "aeriosiq-intake";
export const DB_VERSION = 3;
export const HANDLES = "handles";
export const SESSIONS = "sessions";
export const META = "meta";

export function openIntakeDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev: IDBVersionChangeEvent) => {
      const db = req.result;
      const hadSessions = db.objectStoreNames.contains(SESSIONS);
      if (!db.objectStoreNames.contains(HANDLES)) db.createObjectStore(HANDLES);
      if (!hadSessions)
        db.createObjectStore(SESSIONS, { keyPath: "sessionId" });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
      // M3C-04 — v3: the sets' target tenant, NULL = own
      const oldVersion = typeof ev.oldVersion === "number" ? ev.oldVersion : 0;
      if (hadSessions && oldVersion < 3 && req.transaction)
        migrateSessionsToV3(req.transaction.objectStore(SESSIONS));
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error("IndexedDB open failed"));
    };
  });
}

/** One stored session as the v3 shape: every cluster names its tenant. */
export function sessionToV3(rec: unknown): unknown {
  if (!rec || typeof rec !== "object") return rec;
  const r = rec as { clusters?: unknown };
  if (!Array.isArray(r.clusters)) return rec;
  return {
    ...r,
    clusters: r.clusters.map((c: unknown) =>
      c && typeof c === "object"
        ? {
            ...(c as Record<string, unknown>),
            targetTenantSlug:
              (c as { targetTenantSlug?: unknown }).targetTenantSlug ?? null,
          }
        : c,
    ),
  };
}

function migrateSessionsToV3(store: IDBObjectStore): void {
  const all = store.getAll();
  all.onsuccess = () => {
    for (const rec of all.result as unknown[]) store.put(sessionToV3(rec));
  };
}

export async function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openIntakeDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(store, mode).objectStore(store));
      req.onsuccess = () => {
        resolve(req.result);
      };
      req.onerror = () => {
        reject(req.error ?? new Error("IndexedDB request failed"));
      };
    });
  } finally {
    db.close();
  }
}

/** DI-3-C — read one `meta` value; undefined when absent or unavailable. */
export async function getMeta<T>(key: string): Promise<T | undefined> {
  return tx<T | undefined>(
    META,
    "readonly",
    (s) => s.get(key) as IDBRequest<T | undefined>,
  ).catch(() => undefined);
}

/** DI-3-C — write one `meta` value (a convenience: a failure is swallowed). */
export async function setMeta(key: string, value: unknown): Promise<void> {
  await tx(META, "readwrite", (s) => s.put(value, key)).catch(() => undefined);
}

/** DI-3-D — remove one `meta` value (a convenience: a failure is swallowed). */
export async function deleteMeta(key: string): Promise<void> {
  await tx(META, "readwrite", (s) => s.delete(key)).catch(() => undefined);
}

/**
 * DI-3-D — read-modify-write of one `meta` value in ONE readwrite
 * transaction: IndexedDB serialises readwrite transactions on a store, so two
 * tabs racing for the runner claim cannot both win. `fn` returns the value
 * to store, or `undefined` to leave it as it is. Resolves to what is stored
 * afterwards (`undefined` when IndexedDB is unavailable).
 */
export async function updateMeta<T>(
  key: string,
  fn: (current: T | undefined) => T | undefined,
): Promise<T | undefined> {
  let db: IDBDatabase;
  try {
    db = await openIntakeDb();
  } catch {
    return undefined;
  }
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const store = db.transaction(META, "readwrite").objectStore(META);
      const get = store.get(key) as IDBRequest<T | undefined>;
      get.onsuccess = () => {
        const cur = get.result;
        const next = fn(cur);
        if (next === undefined) {
          resolve(cur);
          return;
        }
        const put = store.put(next, key);
        put.onsuccess = () => {
          resolve(next);
        };
        put.onerror = () => {
          reject(put.error ?? new Error("IndexedDB put failed"));
        };
      };
      get.onerror = () => {
        reject(get.error ?? new Error("IndexedDB get failed"));
      };
    });
  } catch {
    return undefined;
  } finally {
    db.close();
  }
}
